import {
  membershipChangeOptionsQueryFn,
  membershipQueryKeys,
} from "@gymflow/api";
import { formatCurrency, membershipHelper } from "@gymflow/helpers";
import {
  ApiTokenListResponse,
  MembershipBean,
  UserMemberSubscriptionBeanWithMembership,
} from "@gymflow/types";
import { InfiniteData, infiniteQueryOptions } from "@tanstack/react-query";
import { FormSelectInput } from "apps/portal/src/components/atoms";
import { useClubSettings } from "apps/portal/src/providers";
import useGymflowModels from "apps/portal/src/store";
import { useTranslation } from "react-i18next";

import { ChangeMembershipFormValues } from "../types";

export type FormNewMembershipSelectProps = {
  activeMembership: UserMemberSubscriptionBeanWithMembership;
};

export const FormNewMembershipSelect = ({
  activeMembership,
}: FormNewMembershipSelectProps) => {
  const { t } = useTranslation();

  const { club } = useClubSettings();
  const { api } = useGymflowModels();

  return (
    <FormSelectInput<ChangeMembershipFormValues, MembershipBean>
      name="newMembership"
      label={t(
        "page.userMember.tab.userMemberAccount.userMemberMembership.membershipCard.modal.changeMembershipModal.formNewMembershipSelect.label",
      )}
      isMulti={false}
      dataFetchingQuery={() =>
        infiniteQueryOptions({
          queryKey: membershipQueryKeys.list(),
          queryFn: async ({ pageParam }) =>
            await membershipChangeOptionsQueryFn({
              api,
              subscriptionId: activeMembership.id,
              nextPageToken: pageParam,
            }),
          select: (data) => {
            const { pageParams, pages } = data as InfiniteData<
              ApiTokenListResponse<MembershipBean>
            >;

            const transformedPages = pages.map((page) =>
              page.content
                .filter((e) => e.id !== activeMembership?.membershipBean.id)
                ?.map((e) => ({
                  id: e.id,
                  label: e.name,
                  subLabel: `${formatCurrency(
                    e.defaultPrice,
                    club.defaultCurrency,
                  )} ${membershipHelper.getBillingRecurrence({
                    ...e,
                    type: "RECURRING",
                  })}`,
                  value: e,
                })),
            );

            return {
              pageParams,
              pages: transformedPages,
            };
          },
          initialPageParam: undefined,
          getNextPageParam: (lastPage: any) =>
            (lastPage as ApiTokenListResponse<MembershipBean>).nextPageToken,
        })
      }
    />
  );
};
